import React from 'react';
import { Avatar } from '../common/Avatar';
import { TeamBadge } from '../common/TeamBadge';
import { Sparkline } from '../common/Sparkline';
import { StateBar } from '../floor/StateBar';
import { TierBadge } from '../floor/TierBadge';
import { answerRateTone, formatDuration, formatNumber, formatPercent } from '../../utils/format';
import type { FloorRow } from '../../utils/floor';
import type { AgentRow } from '../../types/voip';
interface UserListProps {
  rows: AgentRow[];
  floorById: Map<string, FloorRow>;
  onSelect: (id: string) => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}
const headers: {
  label: string;
  align: 'left' | 'right';
}[] = [{
  label: 'User',
  align: 'left'
}, {
  label: 'Team',
  align: 'left'
}, {
  label: 'Talk time',
  align: 'right'
}, {
  label: 'Calls',
  align: 'right'
}, {
  label: 'Made',
  align: 'right'
}, {
  label: 'Answered',
  align: 'right'
}, {
  label: 'Missed',
  align: 'right'
}, {
  label: 'Answer rate',
  align: 'right'
}, {
  label: 'Avg call',
  align: 'right'
}, {
  label: 'Agent state',
  align: 'left'
}, {
  label: 'Leads',
  align: 'right'
}, {
  label: 'Loans',
  align: 'right'
}, {
  label: 'Score',
  align: 'left'
}, {
  label: 'Trend',
  align: 'left'
}];
export function UserList({
  rows,
  floorById,
  onSelect,
  header,
  footer
}: UserListProps) {
  return <section aria-label="Users list" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card" data-id="element-680" data-mp="3r7hngk3ve51e">
      {header && <div className="border-b border-line px-6 py-3" data-id="element-681" data-mp="2ukl9407k7gj7">{header}</div>}
      <div className="overflow-x-auto" data-id="element-682" data-mp="2ziejz6jt4ypt">
        <table className="w-full min-w-[1380px] text-sm" data-id="element-683" data-mp="33j6qvt57irix">
          <thead data-id="element-684" data-mp="33hn9u0bq8cg3">
            <tr className="border-b border-line bg-canvas/60 text-xs text-muted" data-id="element-685" data-mp="33lb4x3wtl6wx">
              {headers.map(h => <th key={h.label} scope="col" className={`px-3 py-2.5 font-medium first:pl-6 last:pr-6 ${h.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-686" data-mp="iszl8z0m1jpp">
                  {h.label}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-687" data-mp="2jtm9j6ogqord">
            {rows.map(({
            agent,
            stats,
            series
          }) => {
            const f = floorById.get(agent.id);
            return <tr key={agent.id} tabIndex={0} onClick={() => onSelect(agent.id)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(agent.id);
              }
            }} aria-label={`Open ${agent.name}`} className="cursor-pointer transition-colors duration-150 ease-out hover:bg-canvas/70 focus:outline-none focus-visible:bg-canvas" data-id="element-688" data-mp="2tsbs71o6od8o">
                  <td className="py-2 pl-6 pr-3" data-id="element-689" data-mp="1idofick4fwoj">
                    <div className="flex items-center gap-3" data-id="element-690" data-mp="1icdbcbmrkmww">
                      <Avatar name={agent.name} size="sm" data-id="element-691" data-mp="1de3ja9wc62fi" />
                      <div className="leading-tight" data-id="element-692" data-mp="3p2gq9j5umr0r">
                        <p className="whitespace-nowrap font-semibold text-ink" data-id="element-693" data-mp="3p1owmdvtb3d4">{agent.name}</p>
                        <p className="text-xs text-muted" data-id="element-694" data-mp="3ttyr877vgrlq">Ext. {agent.extension}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2" data-id="element-695" data-mp="3k343sziw3lyp"><TeamBadge team={agent.team} data-id="element-696" data-mp="3k4f7z0g8yvqc" /></td>
                  <td className="px-3 py-2 text-right font-bold text-ink" data-id="element-697" data-mp="3k112hjcae6eo">{formatDuration(stats.talkSeconds)}</td>
                  <td className="px-3 py-2 text-right text-ink" data-id="element-698" data-mp="gmewhbbhg87z">{formatNumber(stats.totalCalls)}</td>
                  <td className="px-3 py-2 text-right text-ink" data-id="element-699" data-mp="2n5r76oteyis">{formatNumber(stats.outbound)}</td>
                  <td className="px-3 py-2 text-right text-ink" data-id="element-700" data-mp="7lqgwc6fxzsz">{formatNumber(stats.inbound)}</td>
                  <td className="px-3 py-2 text-right font-medium text-danger" data-id="element-701" data-mp="2ndedg6hjlqb9">{formatNumber(stats.missed)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${answerRateTone(stats.answerRate)}`} data-id="element-702" data-mp="2scqzip0latcs">{formatPercent(stats.answerRate)}</td>
                  <td className="px-3 py-2 text-right text-muted" data-id="element-703" data-mp="37n5rrno4nbpl">{formatDuration(stats.avgSeconds)}</td>
                  <td className="px-3 py-2" data-id="element-704" data-mp="32ttgef8gl1es">
                    {f ? <div className="w-32" data-id="element-705" data-mp="2xwun1ywmcyhn">
                        <StateBar day={f.day} size="sm" data-id="element-706" data-mp="2tug4lfwv40qw" />
                        <p className="mt-1 text-[11px] text-muted" data-id="element-707" data-mp="2tvrapmds5kl6">
                          <span className="font-semibold text-ink" data-id="element-708" data-mp="3ql8j4945dkqk">{formatPercent(f.focus)}</span> focus
                        </p>
                      </div> : <span className="text-subtle" data-id="element-709" data-mp="3hjscmcgge2rm">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-ink" data-id="element-710" data-mp="3cl7j0vvpihc5">{f ? f.day.leads : '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-ink" data-id="element-711" data-mp="264ltb2s6yk8r">{f ? f.day.loans : '—'}</td>
                  <td className="px-3 py-2" data-id="element-712" data-mp="21628j5pc3ehz">
                    {f && <span className="flex items-center gap-2" data-id="element-713" data-mp="21853m45cfxtk">
                        <span className="w-6 text-right font-bold text-ink" data-id="element-714" data-mp="gpb3ax20vnew">{f.score}</span>
                        <TierBadge tier={f.tier} data-id="element-715" data-mp="lnmfvfjpcc0t" />
                      </span>}
                  </td>
                  <td className="py-2 pl-3 pr-6" data-id="element-716" data-mp="pn4vbvsr7uw9"><Sparkline data={series} tone={agent.trend >= 0 ? 'positive' : 'danger'} width={72} height={24} data-id="element-717" data-mp="uf4v9o8tqnsu" /></td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
      {footer && <div className="border-t border-line px-6 py-3" data-id="element-718" data-mp="14cj7fbnpsqfc">{footer}</div>}
    </section>;
}